import { useLazyQuery, useMutation } from '@apollo/client/react';
import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import { UserSignupRegisterForm } from '@src/components/sco/Account/UserSignupRegisterForm';
import { DiagnosticColumns } from '@src/components/sco/Funding/FundingLandingDiagnostic/DiagnosticShell';
import { AccentHeading, formatCompactRaise, Panel, ProofStats, WhyWeAsk } from '@src/components/sco/Funding/FundingLandingDiagnostic/shared';
import { useRequestFunnelContext } from '@src/components/sco/RequestFunnel/RequestFunnelContext';
import { ScoSignupRequest } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import tracker from '@src/lib/tracker';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { LuChevronRight } from 'react-icons/lu';
const GROUP_REQUEST_INVESTOR_SEARCH_QUERY = gql`
  query FundingLandingDiagnosticGroupRequest($groupRequestId: String!) {
    groupRequest(groupRequestId: $groupRequestId) {
      id
      groupRequestId
      investorSearchId
    }
  }
`;
const UPSERT_INVESTOR_SEARCH_MUTATION = gql`
  mutation FundingLandingDiagnosticUpsertInvestorSearch($input: UpsertInvestorSearchInput!) {
    upsertInvestorSearch(input: $input) {
      investorSearch {
        id
        investorSearchId
      }
    }
  }
`;
type MatchCriteria = Pick<Partial<ScoSignupRequest>, 'companyElevatorPitch' | 'fundingGoalInDollars' | 'investorIndustryTagTitles' | 'location' | 'locationCity' | 'locationCountryKey' | 'locationState'>;
export const RegisterStep: React.FC = () => {
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const {
    signupRequest,
    signupRequestId,
    saveSignupRequest,
    isSaving
  } = useRequestFunnelContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activeSignupRequestId = signupRequest.signupRequestId ?? signupRequestId;
  const matchHint = signupRequest.investorIndustryTagTitles?.[0] ? `${formatCompactRaise(signupRequest.fundingGoalInDollars)} ${signupRequest.investorIndustryTagTitles[0]}` : 'your raise';
  const [fetchGroupRequest] = useLazyQuery(GROUP_REQUEST_INVESTOR_SEARCH_QUERY, {
    fetchPolicy: 'network-only'
  });
  const [upsertInvestorSearch] = useMutation(UPSERT_INVESTOR_SEARCH_MUTATION);
  useEffect(() => {
    tracker.funnelStep(5);
  }, []);
  const resolveInvestorSearchId = async (groupRequestId: string): Promise<string | null> => {
    const {
      data,
      error
    } = await fetchGroupRequest({
      variables: {
        groupRequestId
      }
    });
    if (error) {
      throw error;
    }
    return data?.groupRequest?.investorSearchId || null;
  };

  /**
   * Commit alone creates a GroupRequest + InvestorSearch that often lacks tag/location for
   * tag-only funnels. Mirror WhoWillFundMeContinue: seed criteria onto the search and reprocess.
   */
  const seedInvestorSearch = async (groupRequestId_0: string, criteria: MatchCriteria) => {
    let investorSearchId = await resolveInvestorSearchId(groupRequestId_0);
    if (!investorSearchId) {
      for (let i = 0; i < 5 && !investorSearchId; i++) {
        await new Promise(r => setTimeout(r, 400 * (i + 1)));
        investorSearchId = await resolveInvestorSearchId(groupRequestId_0);
      }
    }
    if (!investorSearchId) {
      throw new Error('Investor matching is not ready yet. Please try again.');
    }
    const industryTitles = (criteria.investorIndustryTagTitles || []).filter(Boolean);
    await upsertInvestorSearch({
      variables: {
        input: {
          investorSearchId,
          fundingGoalInDollars: criteria.fundingGoalInDollars || undefined,
          companyElevatorPitch: criteria.companyElevatorPitch || (industryTitles.length ? industryTitles.join(', ') : undefined),
          location: criteria.location || undefined,
          locationCity: criteria.locationCity || undefined,
          locationState: criteria.locationState || undefined,
          locationCountryKey: criteria.locationCountryKey || undefined,
          requiredInvestorTagTitles: industryTitles.length ? industryTitles : undefined,
          reprocess: true
        }
      }
    });
  };
  const onUnlock = ({
    skipAuth = false
  } = {}) => {
    if (!currentUser?.isRegistered && !skipAuth) {
      return;
    }

    // Capture before any async save/commit can replace funnel state.
    const criteria_0: MatchCriteria = {
      companyElevatorPitch: signupRequest.companyElevatorPitch,
      fundingGoalInDollars: signupRequest.fundingGoalInDollars,
      investorIndustryTagTitles: signupRequest.investorIndustryTagTitles,
      location: signupRequest.location,
      locationCity: signupRequest.locationCity,
      locationCountryKey: signupRequest.locationCountryKey,
      locationState: signupRequest.locationState
    };
    setIsSubmitting(true);
    void saveSignupRequest(sr => {
      void (async () => {
        if (!sr?.groupRequestId) {
          alert('Something went wrong. Please try again.');
          setIsSubmitting(false);
          return;
        }
        try {
          await seedInvestorSearch(sr.groupRequestId, {
            ...criteria_0,
            fundingGoalInDollars: criteria_0.fundingGoalInDollars ?? sr.fundingGoalInDollars,
            investorIndustryTagTitles: criteria_0.investorIndustryTagTitles?.length > 0 ? criteria_0.investorIndustryTagTitles : sr.investorIndustryTagTitles,
            location: criteria_0.location || sr.location,
            locationCity: criteria_0.locationCity || sr.locationCity,
            locationState: criteria_0.locationState || sr.locationState,
            locationCountryKey: criteria_0.locationCountryKey || sr.locationCountryKey
          });
          router.push(`/requests/manage/${sr.groupRequestId}/investor-matching/matches`);
        } catch (err: unknown) {
          console.error('FundingLandingDiagnostic seedInvestorSearch failed', err);
          const message = err instanceof Error ? err.message : null;
          alert(message || 'Something went wrong setting up your matches. Please try again.');
          setIsSubmitting(false);
        }
      })();
    }, {
      commitToGroupRequest: true,
      // Do not send requestingUserId from client — after registerUser the React
      // currentUser is often still the guest UUID until re-render, which fails the
      // users FK. Server claims from the session (set by registerUser.login).
      additionalFields: signupRequest.additionalFields,
      fundingGoalInDollars: criteria_0.fundingGoalInDollars,
      investorIndustryTagTitles: criteria_0.investorIndustryTagTitles,
      location: criteria_0.location,
      locationCity: criteria_0.locationCity,
      locationCountryKey: criteria_0.locationCountryKey,
      locationPostalCode: signupRequest.locationPostalCode,
      locationState: criteria_0.locationState
    }, true);
  };
  return <DiagnosticColumns left={<>
          <AccentHeading accent={'your full list.'} line1={'One step to'} />
          <WhyWeAsk title={'Why register'} items={[<>Unlock all investor matches for {matchHint}</>, 'Save your step-by-step Funding Plan', 'Start automated outreach']} />
          <ProofStats />
        </>} right={<>
          <Panel>
            <Heading color={'fg'} marginBottom={2} size={'md'} textAlign={'start'}>
              Register to unlock your matches
            </Heading>
            {currentUser?.isRegistered ? <VStack gap={3} paddingY={4}>
                <Text color={'fg.muted'} fontSize={'sm'}>
                  Welcome back, {currentUser.displayName}!
                </Text>
                <Button loading={isSubmitting || isSaving} size={'lg'} variant={'cta'} width={'full'} onClick={() => onUnlock({
          skipAuth: true
        })}>
                  See My Investor Matches
                  <LuChevronRight />
                </Button>
              </VStack> : <Box textAlign={'left'} width={'full'}>
                <UserSignupRegisterForm focusTrigger={signupRequest.groupRequestTypeKey} hideAlreadyLoggedInAlert={true} hideFullName={true} inputProps={{
          size: 'xl'
        }} signupRequestId={activeSignupRequestId} afterLoginTokenSent={async () => {
          tracker.emailSubmitted();
          tracker.funnelStep(6);
        }} afterSuccess={async () => {
          tracker.emailSubmitted();
          tracker.funnelStep(6);
          onUnlock({
            skipAuth: true
          });
        }} containerProps={{
          background: 'none',
          border: 0,
          boxShadow: 'none',
          margin: 0,
          padding: 0
        }} socialLoginButtonsProps={{
          buttonLayout: 'google-callout-grid',
          actionLabel: 'Continue',
          buttonProps: {
            all: {
              size: 'md',
              width: 'full'
            }
          }
        }} submitButtonProps={{
          children: 'See My Matches',
          disabled: isSaving || !activeSignupRequestId,
          loading: isSubmitting || isSaving,
          variant: 'cta',
          size: 'lg',
          width: 'full'
        }} />
              </Box>}
          </Panel>
        </>} />;
};